import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "primereact/button";
import DashboardLayout from "../../../layouts/DashboardLayout";
import Loading from "../../../../../components/Loading/Loading";
import localeText from "../../../../../locale";
import { formatPrice } from "../../../../../utilities/formatPrice";
import { EXTRAPAY_PATH, ORDERS_PER_CYCLE } from "./constants";
import { getOrdersProgressLabel } from "./cycleCopy";
import { formatExtrapayDateTime, formatExtrapayRate } from "./formatExtrapay";
import { getCurrentCyclePurchases } from "./extrapayPoints";
import useExtrapayOverview from "./hooks/useExtrapayOverview";
import useExtrapayPurchases from "./hooks/useExtrapayPurchases";
import useExtrapayConversions from "./hooks/useExtrapayConversions";
import { getPreviousCycleConversions } from "./mapConversions";
import ExtrapayBreadcrumb from "./components/ExtrapayBreadcrumb";
import ExtrapayPageHeader from "./components/ExtrapayPageHeader";
import ExtrapayDetailRows from "./components/ExtrapayDetailRows";
import ExtrapayStatusBadge from "./components/ExtrapayStatusBadge";
import ExtrapayTable from "./components/ExtrapayTable";
import PreviousCyclesTable from "./components/PreviousCyclesTable";
import PurchasePointsDialog from "./components/PurchasePointsDialog";

const ExtrapayCyclePage = () => {
  const history = useHistory();
  const overview = useExtrapayOverview();
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const { purchases, isLoading } = useExtrapayPurchases({
    cycle: overview.cycle,
    ordersInCycle: overview.ordersInCycle,
    enabled: !overview.isLoading && !overview.hasError,
  });
  const conversions = useExtrapayConversions();
  const cyclePurchases = getCurrentCyclePurchases(purchases, overview.ordersInCycle);
  const accumulating = overview.ordersInCycle < ORDERS_PER_CYCLE;

  const columns = [
    {
      key: "purchase",
      header: localeText("extrapayFlowPurchases"),
      render: (row) => localeText("extrapayFlowPurchaseOrigin", { id: row.id }),
    },
    {
      key: "date",
      header: localeText("extrapayFlowDate"),
      render: (row) => formatExtrapayDateTime(row.date),
    },
    {
      key: "points",
      header: localeText("extrapayFlowPoints"),
      render: (row) => localeText("extrapayFlowSignedPoints", { amount: formatPrice(row.extrapayPoints) }),
    },
    {
      key: "details",
      header: "",
      render: (row) => (
        <button
          type="button"
          className="p-0 border-none bg-transparent cursor-pointer"
          style={{ color: "#335CFF" }}
          onClick={() => setSelectedPurchase(row)}
        >
          {localeText("extrapayFlowViewPoints")}
        </button>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <ExtrapayBreadcrumb
        items={[
          { labelKey: "financial", to: "/dashboard-financeiro" },
          { labelKey: "extrapayFlowTitle", to: EXTRAPAY_PATH },
          { labelKey: "extrapayFlowCycleCrumb" },
        ]}
      />
      <ExtrapayPageHeader
        title={localeText("extrapayFlowCycleLabel", { cycle: overview.cycle || "—" })}
        subtitle={localeText("extrapayFlowCycleSubtitle")}
      />

      {overview.isLoading ? (
        <div className="flex justify-content-center py-6">
          <Loading classNameIcon="text-6xl" />
        </div>
      ) : overview.hasError ? (
        <p className="text-sm">{localeText("extrapayFlowLoadError")}</p>
      ) : (
        <>
          <div className="border-round-lg p-4" style={{ border: "1px solid var(--theme-border, #EBEBEB)" }}>
            <ExtrapayStatusBadge
              labelKey={accumulating ? "extrapayFlowStatusInProgress" : "extrapayFlowStatusConfirmed"}
              tone={accumulating ? "warning" : "success"}
            />
            <ExtrapayDetailRows
              rows={[
                { label: localeText("extrapayFlowPurchases"), value: getOrdersProgressLabel(overview.ordersInCycle) },
                { label: localeText("extrapayFlowAccumulatedFee"), value: formatExtrapayRate(overview.conversionRate) },
                {
                  label: localeText("extrapayFlowPointsToConvert"),
                  value: formatPrice(overview.pointsBalance),
                },
                {
                  label: localeText("extrapayFlowSituation"),
                  value: localeText(
                    accumulating ? "extrapayFlowStatusAccumulating" : "extrapayFlowCycleComplete",
                  ),
                },
              ]}
            />
          </div>

          <h3 className="text-base mt-4">{localeText("extrapayFlowCyclePointsTitle")}</h3>
          <ExtrapayTable
            columns={columns}
            rows={isLoading ? [] : cyclePurchases}
            emptyMessage={isLoading ? "" : localeText("extrapayFlowPointsEmpty")}
            rowKey={(row) => row.id}
          />

          <h3 className="text-base mt-4">{localeText("extrapayFlowPreviousCycles")}</h3>
          <PreviousCyclesTable
            cycles={getPreviousCycleConversions(conversions.conversions, overview.cycle)}
            isLoading={conversions.isLoading}
          />

          <Button
            className="p-button-text mt-3 px-0"
            label={localeText("extrapayFlowBackToExtrapay")}
            onClick={() => history.push(EXTRAPAY_PATH)}
          />
          <PurchasePointsDialog
            purchase={selectedPurchase}
            onHide={() => setSelectedPurchase(null)}
          />
        </>
      )}
    </DashboardLayout>
  );
};

export default ExtrapayCyclePage;
